import {
  Description,
  FileUploadOutlined,
  PlayCircleOutline,
  Videocam,
  CloudQueue,
  ScreenShareOutlined,
  StopCircleOutlined,
  WarningAmber,
  ErrorOutline,
} from '@mui/icons-material';
import { CollapsibleBox } from '@/components/common/collapsible-box.tsx';
import { useCollapsibleGroup } from '@/components/common/collapsible-group.context';
import ManageSessionMediaForm, {
  type ManageSessionMediaFormRef,
} from '@/components/events/detail/tabs/common/media/manage-media.form.tsx';
import { PreviewModal } from '@/components/preview/preview-modal';
import { LiveKitPreviewModal } from '@/components/preview/livekit-preview-modal';
import { PlayCueConfirmationModal } from '@/components/media/play-cue-confirmation-modal';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import { SlideControlButton } from '@/components/events/detail/tabs/common/media/slide-control-button.tsx';
import { SpeakerNotesDialog } from '@/components/events/detail/tabs/common/media/speaker-notes-dialog.tsx';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import { useControlDeviceMutation } from '@/state/mutations/player/use-control-device';
import { useControlPreviewMutation } from '@/state/mutations/player/use-control-preview';
import { enrichPlayerSyncDataWithSequencedPlayback } from '@/helpers/preview/preview-sequenced-playback.helper';
import {
  resolveStartSlideIndexForTake,
  seedLiveSlideIndexFromPreviewLayers,
  seedLocalMediaPreviewFromDurableResume,
} from '@/helpers/resolve-start-slide-index-for-take.helper';
import { useUpdateMediaMutation } from '@/state/mutations/media/use-update-media.ts';
import { useMediaUpload } from '@/hooks/use-media-upload.ts';
import { useMediaUploadQuotaModals } from '@/hooks/use-media-upload-quota-modals.ts';
import MediaUploadFeedback from '@/components/media/media-upload-feedback.tsx';
import InsufficientStorageContactModal from '@/components/events/detail/tabs/common/media/insufficient-storage-contact.modal.tsx';
import { getEventIdForMediaUploadQuota, getMediaUploadErrorCode } from '@/helpers/media-upload-quota.helper.ts';
import { useApproveMediaAssetMutation } from '@/state/mutations/media/use-approve-media-asset.ts';
import { useDeleteMediaMutation } from '@/state/mutations/media/use-delete-media.ts';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { MEDIA_PARENT_ENTITY, type MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import { PLAY_TYPE, type ControlDeviceDto } from '@/types/dtos/player/request/control-device.dto';
import { useMemo, useState, useRef, useEffect, useCallback, type ChangeEvent, type ReactNode } from 'react';
import { VisuallyHiddenInput } from '@/components/common/visually-hidden.input.tsx';
import { MediaItemStatusIcon } from '@/components/events/detail/tabs/common/media/media-item-status-icon.tsx';
import { MediaItemOverflowMenu } from '@/components/events/detail/tabs/common/media/media-item-overflow-menu.tsx';
import { MediaItemAutoLoopDwellButton } from '@/components/events/detail/tabs/common/media/media-item-auto-loop-dwell-button.tsx';
import { MediaProcessingInstructionsModal } from '@/components/media/media-processing-instructions.modal.tsx';
import { PlaylistAdvanceModeMenu } from '@/components/events/detail/tabs/common/media/playlist-advance-mode-menu.tsx';
import { PlaylistNextShowModeMenu } from '@/components/events/detail/tabs/common/media/playlist-next-show-mode-menu.tsx';
import type { VirtualMediaItem } from './media.list';
import {
  CircularProgress,
  useTheme,
  Box,
  TextField,
  Stack,
  IconButton,
  ClickAwayListener,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddMediaStorageModal from '@/components/events/detail/tabs/common/media/add-media-storage.modal.tsx';
import { useFlowStore } from '@/state/stores/use-flow-store';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { hasSlideSupport, resolveSlideControlMediaType } from '@/constants/media-types';
import { useMediaPermissions } from '@/hooks/use-media-permissions';
import { useUpdateMediaPlaylistRefAdvanceModeMutation } from '@/state/mutations/media/use-update-media-playlist-ref-advance-mode.ts';
import type { MediaPlaylistAdvanceMode } from '@/types/dtos/media/media-playlist-advance-mode.enum';
import { resolveMediaPlaylistAdvanceMode } from '@/helpers/media-playlist-advance-mode.helper';
import { resolvePlaylistNextShowMode } from '@/helpers/media-playlist-next-show-mode.helper';
import type { PlaylistNextShowMode } from '@/types/dtos/media/playlist-next-show-mode.enum';
import {
  FLOW_CONTROL_MEDIA_ACTIONS_RAIL_WIDTH_PX,
  type MediaListDisplayVariant,
} from '@/components/flows/detail/flow-detail.types';
import { useFlowControlStaging } from './flow-control-staging.context';
import { FlowControlPlayActionIcon } from './flow-control-play-action-icon';
import {
  getPerformItemRowStyles,
  getPerformSelectableRowHoverSx,
  isCueItemPlayReady,
  isCuePreloadIndicatorPhase,
  isPerformItemOnAir,
  resolveFlowControlPlayActionState,
  toMediaStagedItemId,
} from '@/helpers/perform-staging.helper';
import { resolveFlowControlSegmentAccent } from '@/helpers/flow-control-segment-accent.helper';
import {
  getLatestPlayableApprovedAsset,
  isAssetAutoApprovable,
  isAssetPreviewable,
  resolvePreviewAssetForControl,
} from '@/helpers/media-processing-insight.helper';
import {
  resolveMediaAssetStatus,
  resolveMediaItemAggregateStatus,
  resolveMediaItemPlayoutAsset,
} from '@/helpers/media-item-aggregate-status.helper';
import { hasShareableProcessingInstructions } from '@/helpers/media-processing-instructions.helper';
import { useMediaProcessingInstructionsContext } from '@/hooks/use-media-processing-instructions-context';

interface MediaItemProps {
  mediaItem: MediaDto | VirtualMediaItem;
  parentId: string;
  parentType: MediaParentEntity;
  contextParentId?: string;
  contextParentType?: MediaParentEntity;
  selected?: boolean;
  playersPlayingType?: string;
  currentSlideIndex?: number;
  totalSlides?: number;
  speakerNotes?: string;
  /** When true, vertical list spacing is applied by a parent row (e.g. sortable DnD wrapper). */
  disableListItemMargin?: boolean;
  /** Reorder handle rendered inside the header row (playlist DnD). */
  dragHandle?: ReactNode;
  /** Playlist ref id when this row is backed by a playlist usage ref. */
  playlistUsageId?: string;
  playlistId?: string;
  playlistScope?: string;
  playlistSlotId?: string;
  playlistAdvanceMode?: MediaPlaylistAdvanceMode;
  playlistNextShowMode?: PlaylistNextShowMode;
  playlistLoopEnabled?: boolean;
  playlistDwellMs?: number;
  canEditPlaylistAdvanceMode?: boolean;
  displayVariant?: MediaListDisplayVariant;
  /** Flow assets / live playout: slide nav is in the transport bar. */
  hidePerRowSlideControl?: boolean;
  contributorFacing?: boolean;
}

export const MediaItem = ({
  mediaItem,
  parentId,
  parentType,
  contextParentId,
  contextParentType,
  selected,
  playersPlayingType,
  currentSlideIndex,
  totalSlides,
  speakerNotes,
  disableListItemMargin = false,
  dragHandle,
  playlistUsageId,
  playlistId,
  playlistScope,
  playlistSlotId,
  playlistAdvanceMode,
  playlistNextShowMode,
  playlistLoopEnabled,
  playlistDwellMs,
  canEditPlaylistAdvanceMode = false,
  displayVariant = 'default',
  hidePerRowSlideControl = false,
  contributorFacing = false,
}: MediaItemProps) => {
  const isFlowControl = displayVariant === 'flowControl';
  const flowControlStaging = useFlowControlStaging();
  const stagedItemId = toMediaStagedItemId(mediaItem.id);
  const playlistCueContext = useMemo(
    () => (playlistScope ? { scope: playlistScope, slotId: playlistSlotId } : undefined),
    [playlistScope, playlistSlotId],
  );
  const isPerformStaged = isFlowControl
    ? (flowControlStaging?.isStaged(stagedItemId, playlistCueContext) ?? false)
    : false;
  const isPerformPlayed = isFlowControl
    ? (flowControlStaging?.isPlayed(stagedItemId, playlistCueContext) ?? false)
    : false;
  const canPerformInteract = flowControlStaging?.canInteract ?? false;
  const usesAutoCue = flowControlStaging?.usesAutoCue ?? false;
  const cueItemPhase = isFlowControl ? flowControlStaging?.getItemPhase(stagedItemId, playlistCueContext) : undefined;
  const isShowingScopeIdle = isFlowControl
    ? (flowControlStaging?.isShowingScopeIdle(stagedItemId, playlistCueContext) ?? false)
    : false;
  const isNextCueItem = isFlowControl
    ? (flowControlStaging?.isNextCueItem(stagedItemId, playlistCueContext) ?? false)
    : false;
  const isPerformOnAir =
    isFlowControl &&
    isPerformItemOnAir(
      usesAutoCue,
      cueItemPhase,
      isPerformStaged,
      isPerformPlayed,
      flowControlStaging?.isLiveCurrentItem(stagedItemId, playlistCueContext) ?? false,
    );
  const discretePreload = isFlowControl && usesAutoCue && isCuePreloadIndicatorPhase(cueItemPhase);
  // Played media stays preloaded (replayable) → show it as loaded/neutral, not the solid on-air accent.
  const playedStillLoaded = isFlowControl && usesAutoCue && cueItemPhase === 'played';
  // Preload readiness is shown by tinting the play button green instead of a separate dot.
  const isPlayReady = isFlowControl && usesAutoCue && isCueItemPlayReady(cueItemPhase);
  const t = useTranslations();
  const theme = useTheme();
  const notification = useNotifications();
  const { mutateAsync: controlDeviceMutation, isPending: isControlPending } = useControlDeviceMutation();
  const { mutateAsync: controlPreviewMutation, isPending: isPreviewPending } = useControlPreviewMutation();
  const { mutateAsync: updateMediaMutation, isPending: isUpdatePending } = useUpdateMediaMutation();
  const { mutateAsync: approveMediaAssetMutation } = useApproveMediaAssetMutation();
  const { mutateAsync: deleteMediaMutation, isPending: isDeletePending } = useDeleteMediaMutation();
  const {
    mutateAsync: updatePlaylistRefAdvanceMode,
    isPending: isUpdatingPlaylistAdvanceMode,
    variables: updatingPlaylistRefVariables,
  } = useUpdateMediaPlaylistRefAdvanceModeMutation();

  const flow = useFlowStore((s) => s.flow);
  const flowControlOnAirAccent = resolveFlowControlSegmentAccent(Boolean(flow?.liveContext?.isRunning));
  const mediaUpload = useMediaUpload();
  const eventIdForQuota =
    (flow ? getEventIdForMediaUploadQuota(flow, MEDIA_PARENT_ENTITY.FLOW) : undefined) ??
    (parentType === MEDIA_PARENT_ENTITY.EVENT ? parentId : undefined);
  const quotaModals = useMediaUploadQuotaModals(eventIdForQuota);
  const groupContext = useCollapsibleGroup();
  const hasRunningRoom = flow?.liveContext?.isRunning ?? false;
  const eventMode = useEventMode(
    flow?.origin ? (typeof flow.origin === 'string' ? flow.origin : flow.origin.id) : parentId,
  );

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    playType: string;
    playerSyncData: Record<string, unknown>; // here its actually irrelevant what response we get, we just pass it down to preview page of player.
    /** Asset version opened in the modal — drives compatibility-instructions visibility. */
    assetVersion?: number;
  } | null>(null);

  const closePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
    setPreviewLoading(false);
    setPreviewData(null);
  }, []);

  const [liveKitPreviewOpen, setLiveKitPreviewOpen] = useState(false);
  const [liveKitPreviewData, setLiveKitPreviewData] = useState<{
    livekitRoomId: string;
    title: string;
  } | null>(null);

  const [toggleRequestModalOpen, setToggleRequestModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [playConfirmOpen, setPlayConfirmOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const manageMediaFormRef = useRef<ManageSessionMediaFormRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const isVirtual = (mediaItem as VirtualMediaItem)?.isVirtual;
  const [formUploadBusy, setFormUploadBusy] = useState(false);
  const [statusBusyHold, setStatusBusyHold] = useState(false);
  const uploadAssetVersionBaselineRef = useRef(0);
  const awaitingUploadedAssetRef = useRef(false);
  const isUploading = !isVirtual && (mediaUpload.isBusy || formUploadBusy);
  const statusIconBusy = isUploading || statusBusyHold;

  const beginStatusBusyHold = useCallback(() => {
    if (isVirtual) return;
    setStatusBusyHold((prev) => {
      if (prev) {
        return prev;
      }
      awaitingUploadedAssetRef.current = false;
      const assets = (mediaItem as MediaDto).assets ?? [];
      uploadAssetVersionBaselineRef.current = assets.reduce((max, asset) => Math.max(max, asset.version), 0);
      return true;
    });
  }, [isVirtual, mediaItem]);

  const endStatusBusyHold = useCallback(() => {
    awaitingUploadedAssetRef.current = false;
    setStatusBusyHold(false);
  }, []);

  const markAwaitingUploadedAsset = useCallback(() => {
    awaitingUploadedAssetRef.current = true;
    setStatusBusyHold(true);
  }, []);

  const handleFormUploadBusyChange = useCallback(
    (busy: boolean) => {
      setFormUploadBusy(busy);
      if (busy) {
        beginStatusBusyHold();
      }
    },
    [beginStatusBusyHold],
  );

  const handleFormUploadSettled = useCallback(
    (result: 'success' | 'error') => {
      if (result === 'error') {
        endStatusBusyHold();
        return;
      }
      markAwaitingUploadedAsset();
    },
    [endStatusBusyHold, markAwaitingUploadedAsset],
  );

  useEffect(() => {
    if (isUploading) {
      beginStatusBusyHold();
    }
  }, [beginStatusBusyHold, isUploading]);

  useEffect(() => {
    if (mediaUpload.status === 'error') {
      endStatusBusyHold();
    }
  }, [endStatusBusyHold, mediaUpload.status]);

  // Keep the status hourglass until the media list reflects the new asset after a successful upload.
  // If upload ends without success (cancel), drop the hold immediately.
  useEffect(() => {
    if (isVirtual || !statusBusyHold || isUploading) {
      return;
    }
    if (!awaitingUploadedAssetRef.current) {
      endStatusBusyHold();
      return;
    }
    const assets = (mediaItem as MediaDto).assets ?? [];
    const maxVersion = assets.reduce((max, asset) => Math.max(max, asset.version), 0);
    if (maxVersion > uploadAssetVersionBaselineRef.current) {
      endStatusBusyHold();
    }
  }, [endStatusBusyHold, isUploading, isVirtual, mediaItem, statusBusyHold]);

  useEffect(() => {
    if (isVirtual || isFlowControl || !groupContext) {
      return;
    }

    groupContext.setExpandedLocked(mediaItem.id, isUploading);

    return () => {
      groupContext.setExpandedLocked(mediaItem.id, false);
    };
  }, [groupContext, isFlowControl, isUploading, isVirtual, mediaItem.id]);

  const permissions = useMediaPermissions(isVirtual ? undefined : (mediaItem as MediaDto));
  const canTakeToAir = isVirtual || permissions.canPlay;
  const mediaInstructionsContext = useMediaProcessingInstructionsContext(
    isVirtual ? undefined : (mediaItem as MediaDto),
  );
  const disabled = (mediaItem as VirtualMediaItem)?.disabled ?? false;
  const isRequested = !isVirtual && (mediaItem as MediaDto).isRequested === true;

  const enrichPreviewSyncData = useCallback(
    (playerSyncData: Record<string, unknown>) => {
      if (isVirtual) {
        return playerSyncData;
      }
      return enrichPlayerSyncDataWithSequencedPlayback(playerSyncData, {
        mediaId: mediaItem.id,
        loopEnabled: playlistLoopEnabled ?? false,
        dwellMs: playlistDwellMs ?? 5000,
      });
    },
    [isVirtual, mediaItem.id, playlistLoopEnabled, playlistDwellMs],
  );

  const handlePlayMedia = async () => {
    try {
      const playRequest = isVirtual
        ? {
            entityType: (mediaItem as VirtualMediaItem).entityType,
            entityId: (mediaItem as VirtualMediaItem).entityId,
            contextEntityId: parentId,
            contextEntityType: parentType,
            playType: (mediaItem as VirtualMediaItem).playType,
            playerId: (mediaItem as VirtualMediaItem).data?.player?.id,
          }
        : {
            entityType: 'Media' as const,
            entityId: mediaItem.id,
            contextEntityId: contextParentId ?? parentId,
            contextEntityType: contextParentType ?? parentType,
            playType: PLAY_TYPE.MEDIA,
          };

      await controlDeviceMutation(playRequest);

      notification.notifySuccess(t('media.notification.playSuccess'));
    } catch (error) {
      console.error('Control device error:', error);
      notification.notifyError(t('media.notification.playError'));
    }
  };

  const handleToggleRequestClick = () => {
    if (isVirtual) return;
    setToggleRequestModalOpen(true);
  };

  const handleToggleRequestConfirm = async () => {
    if (isVirtual) return;
    const media = mediaItem as MediaDto;
    const newIsRequested = !isRequested;
    // Close immediately — isRequested is patched optimistically in the mutation.
    setToggleRequestModalOpen(false);
    try {
      await updateMediaMutation({ id: media.id, isRequested: newIsRequested });
      notification.notifySuccess(
        t(newIsRequested ? 'media.notification.requestSuccess' : 'media.notification.unrequestSuccess', {
          title: media.title,
        }),
      );
    } catch {
      notification.notifyError(t('media.notification.requestError'));
    }
  };

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (isVirtual || !permissions.canUpload) return;
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    if (files.length === 0) {
      return;
    }

    const media = mediaItem as MediaDto;
    try {
      const res = await mediaUpload.uploadFiles(media.id, files);

      if (permissions.canApprove && isAssetAutoApprovable(res)) {
        try {
          await approveMediaAssetMutation({
            mediaId: media.id,
            assetVersion: res.version,
            isApproved: true,
            parentId,
            parentType,
          });
        } catch (approvalError) {
          console.error('Failed to auto-approve asset:', approvalError);
        }
      }

      notification.notifySuccess(t('media.notification.uploadSuccess', { name: res.name }));
      markAwaitingUploadedAsset();
      mediaUpload.reset();
    } catch (error) {
      if (!quotaModals.handleUploadError(error)) {
        const errorCode = getMediaUploadErrorCode(error);
        const errorMessage = t('media.notification.uploadError');
        const reason = errorCode ? `: ${t(`error.${errorCode}` as TranslationKey)}` : '';
        notification.notifyError(`${errorMessage}${reason}`);
      }
      endStatusBusyHold();
      mediaUpload.resetAfterUploadAttempt(error);
    }
  };

  const handleToggleRequestCancel = () => {
    setToggleRequestModalOpen(false);
  };

  const handleStartEditTitle = () => {
    if (isVirtual) return;
    setEditTitleValue((mediaItem as MediaDto).title);
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditTitleValue('');
  };

  const handleSaveTitle = async () => {
    if (isVirtual) return;
    const media = mediaItem as MediaDto;
    const trimmedValue = editTitleValue.trim();

    if (!trimmedValue) {
      notification.notifyError(t('media.notification.updateError'));
      return;
    }

    if (trimmedValue === media.title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      await updateMediaMutation({ id: media.id, title: trimmedValue });
      setIsEditingTitle(false);
      notification.notifySuccess(t('media.notification.updateSuccess', { title: trimmedValue }));
    } catch {
      notification.notifyError(t('media.notification.updateError'));
    }
  };

  const handleClickAwayTitle = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-inline-title-edit]')) {
      return;
    }
    handleCancelEditTitle();
  };

  const handleDeleteClick = () => {
    if (isVirtual) return;
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (isVirtual) return;
    try {
      await deleteMediaMutation({ mediaId: mediaItem.id });
      setIsDeleteModalOpen(false);
      notification.notifySuccess(t('media.notification.deleteSuccess', { title: mediaItem.title }));
    } catch {
      notification.notifyError(t('media.notification.deleteError'));
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handlePreviewMedia = async () => {
    try {
      const virtualItem = mediaItem as VirtualMediaItem;

      // For virtual LIVE items with connected player, use LiveKit preview
      if (isVirtual && virtualItem.playType === PLAY_TYPE.LIVE && (virtualItem.data?.player?.isConnected ?? false)) {
        const livekitRoomId = `${virtualItem.entityType}:${virtualItem.entityId}:onAir`;
        setLiveKitPreviewData({ livekitRoomId, title: mediaItem.title });
        setLiveKitPreviewOpen(true);
        notification.notifySuccess(t('media.notification.previewSuccess'));
        return;
      }

      if (!isVirtual && !previewAsset) {
        return;
      }

      const previewRequest: ControlDeviceDto = isVirtual
        ? {
            entityType: virtualItem.entityType,
            entityId: virtualItem.entityId,
            contextEntityId: parentId,
            contextEntityType: parentType,
            playType: virtualItem.playType,
          }
        : {
            entityType: 'Media' as const,
            entityId: mediaItem.id,
            contextEntityId: contextParentId ?? parentId,
            contextEntityType: contextParentType ?? parentType,
            playType: PLAY_TYPE.MEDIA,
            assetVersion: previewAsset!.version,
          };

      if (!isVirtual) {
        seedLocalMediaPreviewFromDurableResume(`media:${mediaItem.id}`);
      }
      setPreviewData({
        playType: previewRequest.playType,
        playerSyncData: {},
        assetVersion: isVirtual ? undefined : previewAsset!.version,
      });
      setPreviewLoading(true);
      setPreviewModalOpen(true);

      const response = await controlPreviewMutation(previewRequest);
      const playerSyncData = enrichPreviewSyncData(response.playerSyncData);
      if (!isVirtual) {
        seedLiveSlideIndexFromPreviewLayers(`media:${mediaItem.id}`, playerSyncData);
        seedLocalMediaPreviewFromDurableResume(`media:${mediaItem.id}`);
      }

      setPreviewData({
        playType: previewRequest.playType,
        playerSyncData,
        assetVersion: isVirtual ? undefined : previewAsset!.version,
      });
      setPreviewLoading(false);

      notification.notifySuccess(t('media.notification.previewSuccess'));
    } catch (error) {
      console.error('Preview device error:', error);
      closePreviewModal();
      notification.notifyError(t('media.notification.previewError'));
    }
  };

  const handlePreviewAsset = async (assetVersion: number) => {
    if (isVirtual) return;

    const asset = (mediaItem as MediaDto).assets?.find((item) => item.version === assetVersion);
    if (!asset || !isAssetPreviewable(asset)) {
      return;
    }

    try {
      const previewRequest: ControlDeviceDto = {
        entityType: 'Media' as const,
        entityId: mediaItem.id,
        contextEntityId: contextParentId ?? parentId,
        contextEntityType: contextParentType ?? parentType,
        playType: PLAY_TYPE.MEDIA,
        assetVersion: assetVersion,
      };

      seedLocalMediaPreviewFromDurableResume(`media:${mediaItem.id}`);
      setPreviewData({
        playType: previewRequest.playType,
        playerSyncData: {},
        assetVersion,
      });
      setPreviewLoading(true);
      setPreviewModalOpen(true);

      const response = await controlPreviewMutation(previewRequest);
      const playerSyncData = enrichPreviewSyncData(response.playerSyncData);
      seedLiveSlideIndexFromPreviewLayers(`media:${mediaItem.id}`, playerSyncData);
      seedLocalMediaPreviewFromDurableResume(`media:${mediaItem.id}`);

      setPreviewData({
        playType: previewRequest.playType,
        playerSyncData,
        assetVersion,
      });
      setPreviewLoading(false);

      notification.notifySuccess(t('media.notification.previewSuccess'));
    } catch (error) {
      console.error('Preview asset error:', error);
      closePreviewModal();
      notification.notifyError(t('media.notification.previewError'));
    }
  };
  const isLive = (mediaItem as VirtualMediaItem).playType === PLAY_TYPE.LIVE && hasRunningRoom;
  const isPlayerConnected = (mediaItem as VirtualMediaItem).data?.player.isConnected ?? false;
  const isPresenter = (mediaItem as VirtualMediaItem).data?.player.type === 'screenPublisher';
  const vmStatus = (mediaItem as VirtualMediaItem).data?.player.vmStatus;
  const isVmRunning = vmStatus === 'running';

  const isBaseDisabled = useMemo(() => {
    return ((mediaItem as VirtualMediaItem).playType === PLAY_TYPE.LIVE && !isPlayerConnected) || disabled;
  }, [mediaItem, isPlayerConnected, disabled]);

  const listItemMargin = disableListItemMargin ? 0 : '0.4rem 0';

  const latestApprovedVersion = useMemo(() => {
    const nonVirtualMediaItem = mediaItem as MediaDto;
    if (!nonVirtualMediaItem?.assets) return undefined;
    const approvedAssets = nonVirtualMediaItem.assets.filter((asset) => asset.isApproved);
    if (approvedAssets.length === 0) return undefined;
    // Sort by version descending and get the first (latest) approved asset
    return [...approvedAssets].sort((a, b) => b.version - a.version)[0];
  }, [mediaItem]);

  const playableAsset = useMemo(() => {
    const nonVirtualMediaItem = mediaItem as MediaDto;
    if (!nonVirtualMediaItem?.assets) return undefined;
    return getLatestPlayableApprovedAsset(nonVirtualMediaItem.assets);
  }, [mediaItem]);

  const previewAsset = !isVirtual ? resolvePreviewAssetForControl((mediaItem as MediaDto).assets ?? []) : undefined;
  const localPreviewSlideAsset = !isVirtual
    ? ((previewData?.assetVersion != null
        ? (mediaItem as MediaDto).assets?.find((asset) => asset.version === previewData.assetVersion)
        : undefined) ?? previewAsset)
    : undefined;

  const resolvedPlaylistAdvanceMode = resolveMediaPlaylistAdvanceMode(
    playlistAdvanceMode,
    latestApprovedVersion?.fileType,
  );

  const isPerformSkippedEffective =
    isFlowControl &&
    usesAutoCue &&
    flowControlStaging?.mediaAgentAvailable === false &&
    !isVirtual &&
    playableAsset?.fileType === 'presentation' &&
    playableAsset?.processingInsight?.playoutReady !== true;

  // Presentation decks with renditions stay playable during an outage — say where slides come from
  // instead of leaving the row looking skipped.
  const showBackupSlidesHint =
    isFlowControl &&
    usesAutoCue &&
    (flowControlStaging?.backupSlidesActive ?? false) &&
    !isVirtual &&
    playableAsset?.fileType === 'presentation' &&
    playableAsset?.processingInsight?.playoutReady === true;

  const handleFlowControlToggle = () => {
    if (!isFlowControl || !flowControlStaging) return;
    const action = resolveFlowControlPlayActionState({
      usesAutoCue,
      cueItemPhase,
      isItemCueCommandPending: flowControlStaging.isItemCueCommandPending(stagedItemId, playlistCueContext),
      canInteract: canPerformInteract,
      isSkipped: isPerformSkippedEffective,
      isScopeIdle: isShowingScopeIdle,
      isOnAir: isPerformOnAir,
    });
    if (action.disabled) return;
    if (action.showStop) {
      flowControlStaging.toggleItem(stagedItemId, playlistCueContext);
      return;
    }
    if (!canTakeToAir) return;
    if (isNextCueItem) {
      flowControlStaging.advanceNextCue();
      return;
    }
    setPlayConfirmOpen(true);
  };

  const closePreviewAfterPlayChoice = () => {
    closePreviewModal();
    setLiveKitPreviewOpen(false);
    setLiveKitPreviewData(null);
  };

  const capturePreviewStartSlideIndex = () =>
    resolveStartSlideIndexForTake(stagedItemId, { playlistContext: playlistCueContext });

  const handleConfirmJumpCue = () => {
    if (!canTakeToAir) return;
    const startSlideIndex = capturePreviewStartSlideIndex();
    setPlayConfirmOpen(false);
    closePreviewAfterPlayChoice();
    if (isFlowControl && flowControlStaging) {
      flowControlStaging.playCueItem(stagedItemId, 'jump', playlistCueContext, startSlideIndex);
      return;
    }
    void handlePlayMedia();
  };

  const handleConfirmPlayInsert = () => {
    if (!canTakeToAir) return;
    const startSlideIndex = capturePreviewStartSlideIndex();
    setPlayConfirmOpen(false);
    closePreviewAfterPlayChoice();
    if (isFlowControl && flowControlStaging) {
      flowControlStaging.playCueItem(stagedItemId, 'insert', playlistCueContext, startSlideIndex);
      return;
    }
    void handlePlayMedia();
  };

  const handleConfirmPlay = () => {
    if (!canTakeToAir) return;
    setPlayConfirmOpen(false);
    closePreviewAfterPlayChoice();
    void handlePlayMedia();
  };

  const requestPlayFromPreview = () => {
    if (!canTakeToAir) return;
    if (isFlowControl && isNextCueItem && flowControlStaging) {
      flowControlStaging.advanceNextCue();
      closePreviewAfterPlayChoice();
      return;
    }
    setPlayConfirmOpen(true);
  };

  const showCuePlayModes = isFlowControl && usesAutoCue;

  const getSxStyles = () => {
    if (isFlowControl) {
      return {
        'm': listItemMargin,
        'cursor': 'default',
        'overflow': 'hidden',
        '& > .MuiStack-root': {
          flexWrap: 'nowrap',
          minWidth: 0,
        },
        ...getPerformItemRowStyles(isPerformStaged, isPerformPlayed, isPerformOnAir, {
          discretePreload,
          suppressOnAirBackground: true,
          scopeIdle: isShowingScopeIdle,
          onAirAccent: flowControlOnAirAccent,
          playedStillLoaded,
          isNextCue: isNextCueItem,
        }),
        ...(isPerformSkippedEffective ? { opacity: 0.45 } : {}),
        ...getPerformSelectableRowHoverSx(canPerformInteract, isPerformStaged, isPerformPlayed, isPerformOnAir, {
          discretePreload,
          suppressOnAirBackground: true,
          scopeIdle: isShowingScopeIdle,
          onAirAccent: flowControlOnAirAccent,
          playedStillLoaded,
          isNextCue: isNextCueItem,
        }),
      } as SxProps<Theme>;
    }

    if (isVirtual) {
      const baseStyles: SxProps<Theme> = {
        'm': listItemMargin,
        'border': '2px dashed',
        'borderColor': 'divider',
        '& .MuiTypography-root': {
          color: selected ? 'success.main' : 'text.disabled',
        },
      };

      if (selected) {
        baseStyles.borderColor = 'success.main';
        baseStyles.color = 'success.main';
        baseStyles.backgroundColor = 'success.subtle';
      }

      if (isLive) {
        return {
          ...baseStyles,
          ...(isPresenter
            ? {
                position: 'relative',
              }
            : {}),
          'position': 'relative',
          'overflow': 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: `linear-gradient(90deg, transparent, ${theme.palette.error.main}20, transparent)`,
            animation: 'liveShimmer 2s infinite',
            zIndex: 0,
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
          '@keyframes liveShimmer': {
            '0%': {
              left: '-100%',
            },
            '100%': {
              left: '100%',
            },
          },
        };
      }

      return baseStyles;
    }

    // For real media items, apply selection styles
    const baseStyles: SxProps<Theme> = {
      m: listItemMargin,
    };

    if (selected) {
      baseStyles.border = '2px solid';
      baseStyles.borderColor = 'success.main';
      baseStyles.backgroundColor = 'success.subtle';
      baseStyles.color = 'success.main';
    }

    return baseStyles;
  };

  const latestNonApprovedAsset = useMemo(() => {
    const nonVirtualMediaItem = mediaItem as MediaDto;
    if (!nonVirtualMediaItem?.assets || nonVirtualMediaItem.assets.length === 0) return undefined;
    // Prefer the newest non-approved version (e.g. replacement still processing) even when an
    // older approved asset remains playable.
    const nonApprovedAssets = nonVirtualMediaItem.assets.filter((asset) => !asset.isApproved);
    if (nonApprovedAssets.length === 0) return undefined;
    return [...nonApprovedAssets].sort((a, b) => b.version - a.version)[0];
  }, [mediaItem]);

  const collapsedRowIconAsset = useMemo(() => {
    if (isVirtual) {
      return undefined;
    }
    // While the latest version is uploading/processing, bind the row icon to that asset so status
    // resolution does not fall back to the older approved playout file.
    if (
      latestNonApprovedAsset &&
      resolveMediaAssetStatus(latestNonApprovedAsset).showSpinner &&
      (!latestApprovedVersion || latestNonApprovedAsset.version > latestApprovedVersion.version)
    ) {
      return latestNonApprovedAsset;
    }
    return previewAsset ?? latestApprovedVersion ?? latestNonApprovedAsset;
  }, [isVirtual, latestApprovedVersion, latestNonApprovedAsset, previewAsset]);

  const hasAssets = useMemo(() => {
    const nonVirtualMediaItem = mediaItem as MediaDto;
    return !isVirtual && nonVirtualMediaItem?.assets && nonVirtualMediaItem.assets.length > 0;
  }, [isVirtual, mediaItem]);

  const aggregateStatus = useMemo(
    () => (!isVirtual ? resolveMediaItemAggregateStatus(mediaItem as MediaDto) : undefined),
    [isVirtual, mediaItem],
  );

  const canOpenInstructions = useMemo(() => {
    if (isVirtual || !aggregateStatus?.insight) {
      return false;
    }
    // Row affordance: open instructions when not yet approved (approved items open preview instead).
    return hasShareableProcessingInstructions(aggregateStatus.insight) && aggregateStatus.status !== 'approved';
  }, [aggregateStatus, isVirtual]);

  /**
   * Trailing warn/error control — independent of approval / left success icon.
   * Prefer latest asset; fall back to playout when latest has no shareable issue.
   */
  const compatibilityIssueAffordance = useMemo(() => {
    if (isVirtual) {
      return undefined;
    }
    const media = mediaItem as MediaDto;
    const assets = media.assets ?? [];
    if (assets.length === 0) {
      return undefined;
    }
    const latest = [...assets].sort((a, b) => b.version - a.version)[0];
    const playout = previewAsset ?? resolveMediaItemPlayoutAsset(media);
    const candidates = [latest, playout].filter(
      (asset, index, list): asset is NonNullable<typeof asset> =>
        Boolean(asset) && list.findIndex((item) => item?.version === asset?.version) === index,
    );

    let warning: { severity: 'warning'; insight: ProcessingInsightDto; assetVersion: number } | undefined;

    for (const asset of candidates) {
      const status = resolveMediaAssetStatus(asset);
      if (!status.insight || !hasShareableProcessingInstructions(status.insight)) {
        continue;
      }
      if (status.tone === 'error') {
        return { severity: 'error' as const, insight: status.insight, assetVersion: asset.version };
      }
      if (status.tone === 'warning') {
        warning ??= { severity: 'warning', insight: status.insight, assetVersion: asset.version };
      }
    }
    return warning;
  }, [isVirtual, mediaItem, previewAsset]);

  const canOpenCompatibilityIssue = Boolean(compatibilityIssueAffordance && mediaInstructionsContext);

  const rowInstructionsInsight = compatibilityIssueAffordance?.insight;

  // Preview modal: instructions for the opened asset version, whether approved or not.
  const previewInstructions = useMemo(() => {
    if (isVirtual || !mediaInstructionsContext || !previewData) {
      return undefined;
    }
    const media = mediaItem as MediaDto;
    const openedAsset =
      previewData.assetVersion != null
        ? media.assets?.find((asset) => asset.version === previewData.assetVersion)
        : previewAsset;
    // Prefer the opened asset; fall back to aggregate only when that insight is missing or not shareable.
    const insight =
      [openedAsset?.processingInsight, aggregateStatus?.insight].find((candidate) =>
        hasShareableProcessingInstructions(candidate),
      ) ??
      openedAsset?.processingInsight ??
      aggregateStatus?.insight;
    if (!insight || !hasShareableProcessingInstructions(insight)) {
      return undefined;
    }
    return {
      insight,
      context: {
        ...mediaInstructionsContext,
        assetVersion: openedAsset?.version ?? previewData.assetVersion ?? mediaInstructionsContext.assetVersion,
      },
    };
  }, [aggregateStatus?.insight, isVirtual, mediaInstructionsContext, mediaItem, previewAsset, previewData]);

  const hasSlides = useMemo(() => {
    if (isVirtual) return false;

    // Check fileType first
    if (latestApprovedVersion?.fileType) {
      const hasSupport = hasSlideSupport(latestApprovedVersion.fileType);
      return hasSupport;
    }

    // Fallback: Check file extension for backward compatibility
    const fileName = latestApprovedVersion?.name || mediaItem.title;
    const extension = fileName.split('.').pop()?.toLowerCase();
    const hasSlidesByExtension = extension === 'pptx' || extension === 'ppt' || extension === 'pdf';

    return hasSlidesByExtension;
  }, [isVirtual, latestApprovedVersion, mediaItem.title]);

  const titleOpensPreview =
    !isBaseDisabled &&
    !isPreviewPending &&
    (mediaItem as VirtualMediaItem)?.playType !== PLAY_TYPE.LIVE &&
    !isVirtual &&
    permissions.canPreview &&
    previewAsset !== undefined;

  const titleOpensPreviewOrInstructions = titleOpensPreview || canOpenInstructions;

  const renderTitle = () => {
    if (isVirtual) return mediaItem.title;

    if (isEditingTitle) {
      return (
        <ClickAwayListener onClickAway={handleClickAwayTitle}>
          <Stack
            data-inline-title-edit
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flex: 1, minWidth: 0, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              inputRef={titleInputRef}
              variant="standard"
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              disabled={isUpdatePending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editTitleValue.trim()) {
                  e.preventDefault();
                  handleSaveTitle();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancelEditTitle();
                }
              }}
              sx={{
                'flex': 1,
                'minWidth': 0,
                '& .MuiInputBase-root': {
                  'fontSize': '1rem',
                  'fontWeight': 400,
                  'lineHeight': 1.43,
                  'fontFamily': 'Roboto',
                  '&::before': { display: 'none' },
                  '&::after': { display: 'none' },
                  '&:hover::before': { display: 'none' },
                  '&:hover::after': { display: 'none' },
                  '&.Mui-focused::before': { display: 'none' },
                  '&.Mui-focused::after': { display: 'none' },
                },
                '& .MuiInputBase-input': {
                  py: 0,
                  pl: 0,
                  pr: 0.5,
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: 'text.primary',
                },
              }}
            />
            <IconButton
              size="small"
              onClick={handleCancelEditTitle}
              disabled={isUpdatePending}
              sx={{
                'padding': '2px',
                'flexShrink': 0,
                '& > svg': {
                  fontSize: '1.25rem',
                  width: '1.25rem',
                  height: '1.25rem',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CancelOutlinedIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSaveTitle}
              disabled={
                isUpdatePending || !editTitleValue.trim() || editTitleValue.trim() === (mediaItem as MediaDto).title
              }
              sx={{
                'padding': '2px',
                'flexShrink': 0,
                '& > svg': {
                  fontSize: '1.25rem',
                  width: '1.25rem',
                  height: '1.25rem',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CheckCircleIcon />
            </IconButton>
          </Stack>
        </ClickAwayListener>
      );
    }

    const titleText = (
      <Box
        data-inline-title-text
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: titleOpensPreviewOrInstructions ? 'pointer' : 'default',
        }}
      >
        {mediaItem.title}
      </Box>
    );

    if (!showBackupSlidesHint) {
      return titleText;
    }

    return (
      <Stack sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
        {titleText}
        <Typography variant="caption" color="warning.main" sx={{ lineHeight: 1.2 }}>
          {t('flows.detail.control.mediaAgentRecovery.rowBackupSlides', undefined, 'Backup slides')}
        </Typography>
      </Stack>
    );
  };

  const hasPreviewableAsset = previewAsset !== undefined;

  const canPreview =
    !isBaseDisabled &&
    !isPreviewPending &&
    (mediaItem as VirtualMediaItem)?.playType !== PLAY_TYPE.LIVE &&
    (isVirtual || (permissions.canPreview && hasPreviewableAsset));

  /** Matches iconStart: upload when empty or when row is expanded; collapsed shows playout asset status. */
  const isRowExpanded = Boolean(groupContext && groupContext.expandedId === mediaItem.id);
  const isRequestedManagerBadge =
    !isVirtual &&
    aggregateStatus?.status === 'requested' &&
    permissions.canUpdate &&
    !(contributorFacing && permissions.canUpload);

  const mainRowIconAction = useMemo((): 'upload' | 'none' => {
    if (isVirtual) return 'none';
    if (isRequestedManagerBadge) return 'none';
    // Expanded rows always offer upload when permitted (status lives on each asset icon).
    if (isRowExpanded) {
      return permissions.canUpload ? 'upload' : 'none';
    }
    if (!permissions.canUpload) return 'none';
    if (previewAsset || hasAssets) return 'none';
    return 'upload';
  }, [hasAssets, isRequestedManagerBadge, isRowExpanded, isVirtual, permissions.canUpload, previewAsset]);

  const canEditTitle = !isVirtual && !isFlowControl && eventMode !== 'PERFORM' && permissions.canUpdate;

  const canExpandFromStatusIcon = useMemo(() => {
    if (isVirtual || isFlowControl || !groupContext || isUploading || isRowExpanded) {
      return false;
    }
    const status = aggregateStatus?.status;
    if (status === 'pending_approval' || status === 'pending_approval_warning') {
      return true;
    }
    // Limited playout / rejection: details live in the expanded row; severity is on the trailing icon.
    return Boolean(compatibilityIssueAffordance);
  }, [
    aggregateStatus?.status,
    compatibilityIssueAffordance,
    groupContext,
    isFlowControl,
    isRowExpanded,
    isUploading,
    isVirtual,
  ]);

  const handleStatusIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Manager view: requested badge toggles unrequest. Contributor view: upload into the slot.
    if (isRequestedManagerBadge) {
      handleToggleRequestClick();
      return;
    }
    if (mainRowIconAction === 'upload') {
      fileInputRef.current?.click();
      return;
    }
    if (canExpandFromStatusIcon && groupContext) {
      groupContext.toggleExpanded(mediaItem.id);
    }
  };

  const statusIconClickable = useMemo(() => {
    if (isVirtual) {
      return false;
    }
    if (isRequestedManagerBadge) {
      return true;
    }
    if (mainRowIconAction === 'upload') {
      return true;
    }
    return canExpandFromStatusIcon;
  }, [canExpandFromStatusIcon, isRequestedManagerBadge, isVirtual, mainRowIconAction]);

  const handleTitleClick = titleOpensPreviewOrInstructions
    ? (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (
          target.closest('[data-inline-title-edit]') ||
          target.closest('button') ||
          target.closest('[role="button"]')
        ) {
          return;
        }
        if (!target.closest('[data-inline-title-text]')) {
          return;
        }
        e.stopPropagation();
        if (isEditingTitle) {
          return;
        }
        if (titleOpensPreview && canPreview) {
          void handlePreviewMedia();
          return;
        }
        if (canOpenInstructions) {
          setInstructionsOpen(true);
        }
      }
    : undefined;

  const isSequencedRef = hasSlideSupport(latestApprovedVersion?.fileType);

  const resolvedPlaylistNextShowMode = resolvePlaylistNextShowMode(playlistNextShowMode);

  const handleSelectPlaylistAdvanceMode = async (nextMode: MediaPlaylistAdvanceMode) => {
    if (!playlistId || !playlistUsageId || !canEditPlaylistAdvanceMode) {
      return;
    }
    try {
      await updatePlaylistRefAdvanceMode({
        playlistId,
        usageId: playlistUsageId,
        advanceMode: nextMode,
      });
    } catch {
      notification.notifyError(t('media.notification.playlistAdvanceModeError'));
    }
  };

  const handleSelectPlaylistNextShowMode = async (nextMode: PlaylistNextShowMode) => {
    if (!playlistId || !playlistUsageId || !canEditPlaylistAdvanceMode) {
      return;
    }
    try {
      await updatePlaylistRefAdvanceMode({
        playlistId,
        usageId: playlistUsageId,
        nextShowMode: nextMode,
      });
      // If this row is already Next, apply immediately (don't wait for cue-sheet refetch).
      // inherit → copy currently on-air show mode.
      flowControlStaging?.applyNextShowModePreselect(nextMode, stagedItemId, playlistCueContext);
    } catch {
      notification.notifyError(t('media.notification.playlistAdvanceModeError'));
    }
  };

  const resolvedLoopEnabled = playlistLoopEnabled ?? false;
  const resolvedDwellMs = playlistDwellMs ?? 5000;

  const handleSequencedPlaybackChange = async (next: { loopEnabled: boolean; dwellMs: number }) => {
    if (!playlistId || !playlistUsageId || !canEditPlaylistAdvanceMode || !isSequencedRef) {
      return;
    }
    try {
      await updatePlaylistRefAdvanceMode({
        playlistId,
        usageId: playlistUsageId,
        loopEnabled: next.loopEnabled,
        dwellMs: next.dwellMs,
      });
    } catch {
      notification.notifyError(t('media.notification.playlistAdvanceModeError'));
    }
  };

  const isUpdatingPlaylistAdvanceModeSetting =
    isUpdatingPlaylistAdvanceMode && updatingPlaylistRefVariables?.advanceMode !== undefined;
  const isUpdatingPlaylistNextShowModeSetting =
    isUpdatingPlaylistAdvanceMode && updatingPlaylistRefVariables?.nextShowMode !== undefined;
  const isUpdatingPlaylistSequencedPlaybackSetting =
    isUpdatingPlaylistAdvanceMode &&
    (updatingPlaylistRefVariables?.loopEnabled !== undefined || updatingPlaylistRefVariables?.dwellMs !== undefined);

  const playlistAdvanceModeAction =
    isFlowControl && canEditPlaylistAdvanceMode
      ? {
          icon: (
            <PlaylistAdvanceModeMenu
              value={resolvedPlaylistAdvanceMode}
              disabled={isUpdatingPlaylistAdvanceMode}
              loading={isUpdatingPlaylistAdvanceModeSetting}
              testId={`playlist-advance-mode-${mediaItem.id}`}
              onSelect={(mode) => void handleSelectPlaylistAdvanceMode(mode)}
            />
          ),
          unwrapped: true,
          disabled: isUpdatingPlaylistAdvanceMode,
          testId: `playlist-advance-mode-${mediaItem.id}`,
        }
      : null;

  const playlistNextShowModeAction =
    isFlowControl && canEditPlaylistAdvanceMode
      ? {
          icon: (
            <PlaylistNextShowModeMenu
              value={resolvedPlaylistNextShowMode}
              disabled={isUpdatingPlaylistAdvanceMode}
              loading={isUpdatingPlaylistNextShowModeSetting}
              testId={`playlist-next-show-mode-${mediaItem.id}`}
              onSelect={(mode) => void handleSelectPlaylistNextShowMode(mode)}
            />
          ),
          unwrapped: true,
          disabled: isUpdatingPlaylistAdvanceMode,
          testId: `playlist-next-show-mode-${mediaItem.id}`,
        }
      : null;

  // Assets: sequenced auto-loop dwell only. Perform: advance + next show-mode.
  const showSequencedPlaybackMenu =
    !isFlowControl && Boolean(playlistId && playlistUsageId && isSequencedRef && canEditPlaylistAdvanceMode);

  const autoLoopHeaderAction =
    !isFlowControl && canEditPlaylistAdvanceMode && resolvedLoopEnabled
      ? {
          icon: (
            <MediaItemAutoLoopDwellButton
              dwellMs={resolvedDwellMs}
              disabled={isUpdatingPlaylistAdvanceMode}
              loading={isUpdatingPlaylistSequencedPlaybackSetting}
              size="default"
              testId={`playlist-auto-loop-dwell-${mediaItem.id}`}
              onPlaybackChange={(next) => void handleSequencedPlaybackChange(next)}
            />
          ),
          unwrapped: true,
          disabled: isUpdatingPlaylistAdvanceMode,
          testId: `playlist-auto-loop-dwell-${mediaItem.id}`,
        }
      : null;

  const playlistHeaderActions =
    playlistId && playlistUsageId
      ? (isFlowControl ? [playlistAdvanceModeAction, playlistNextShowModeAction] : [autoLoopHeaderAction]).filter(
          (action): action is NonNullable<typeof action> => action != null,
        )
      : undefined;

  const flowControlPlayAction = isFlowControl
    ? resolveFlowControlPlayActionState({
        usesAutoCue,
        cueItemPhase,
        isItemCueCommandPending: flowControlStaging?.isItemCueCommandPending(stagedItemId, playlistCueContext) ?? false,
        canInteract: canPerformInteract,
        isSkipped: isPerformSkippedEffective,
        isScopeIdle: isShowingScopeIdle,
        isOnAir: isPerformOnAir,
      })
    : null;

  const playActionButton =
    isFlowControl && flowControlPlayAction
      ? {
          icon: (
            <FlowControlPlayActionIcon
              showSpinner={flowControlPlayAction.showSpinner}
              showStop={flowControlPlayAction.showStop}
            />
          ),
          onClick: handleFlowControlToggle,
          disabled: flowControlPlayAction.disabled || (!flowControlPlayAction.showStop && !canTakeToAir),
          tooltip: flowControlPlayAction.showStop
            ? t('media.stop' as TranslationKey)
            : t('media.play' as TranslationKey),
          testId: `play-media-${mediaItem.id}`,
          color: flowControlPlayAction.showStop
            ? flowControlOnAirAccent
            : isShowingScopeIdle
              ? ('info' as const)
              : isPlayReady
                ? ('success' as const)
                : undefined,
        }
      : undefined;

  const isImageSequenceAsset =
    latestApprovedVersion?.fileType === 'imageSequence' || latestNonApprovedAsset?.fileType === 'imageSequence';
  const isSequencedMedia = isImageSequenceAsset || (Boolean(playlistId) && isSequencedRef);
  const showUploadAction =
    !isVirtual &&
    permissions.canUpload &&
    eventMode !== 'PERFORM' &&
    !isFlowControl &&
    !mediaUpload.isBusy &&
    !hasAssets &&
    !isSequencedMedia &&
    mainRowIconAction !== 'upload';
  const uploadActionButtons = showUploadAction
    ? [
        {
          icon: mediaUpload.isBusy ? (
            <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
          ) : (
            <FileUploadOutlined />
          ),
          onClick: () => fileInputRef.current?.click(),
          disabled: mediaUpload.isBusy,
          tooltip: t('media.upload' as TranslationKey, { defaultValue: 'Upload' }),
          testId: `upload-media-${mediaItem.id}`,
        },
      ]
    : [];

  const showOverflowMenu = !isVirtual && !isFlowControl && eventMode !== 'PERFORM';
  const showOverflowRename = showOverflowMenu && canEditTitle;
  const showOverflowRequest = showOverflowMenu && !isRequested && !hasAssets && permissions.canUpdate;
  const showOverflowUnrequest = showOverflowMenu && isRequested && permissions.canUpdate;
  const showOverflowAutoLoop = showSequencedPlaybackMenu;
  const showOverflowDelete = showOverflowMenu && permissions.canDelete;

  const handleToggleAutoLoop = () => {
    void handleSequencedPlaybackChange({
      loopEnabled: !resolvedLoopEnabled,
      dwellMs: resolvedDwellMs,
    });
  };

  const defaultActionButtons = [
    ...(canOpenCompatibilityIssue && compatibilityIssueAffordance
      ? [
          {
            icon:
              compatibilityIssueAffordance.severity === 'error' ? (
                <ErrorOutline color="error" />
              ) : (
                <WarningAmber color="warning" />
              ),
            onClick: () => setInstructionsOpen(true),
            tooltip: t('media.processing.instructionsModal.openHint' as TranslationKey),
            testId: `media-compatibility-${compatibilityIssueAffordance.severity}-${mediaItem.id}`,
          },
        ]
      : []),
    ...uploadActionButtons,
    ...(showOverflowMenu &&
    (showOverflowRename || showOverflowRequest || showOverflowUnrequest || showOverflowAutoLoop || showOverflowDelete)
      ? [
          {
            icon: (
              <MediaItemOverflowMenu
                mediaTitle={(mediaItem as MediaDto).title}
                showRename={showOverflowRename}
                showRequest={showOverflowRequest}
                showUnrequest={showOverflowUnrequest}
                showAutoLoop={showOverflowAutoLoop}
                autoLoopEnabled={resolvedLoopEnabled}
                autoLoopDisabled={isUpdatingPlaylistAdvanceMode}
                showDelete={showOverflowDelete}
                deletePending={isDeletePending}
                showNotes={
                  permissions.canUpdate &&
                  (latestApprovedVersion?.fileType === 'presentation' ||
                    latestApprovedVersion?.fileType === 'pdf' ||
                    latestApprovedVersion?.fileType === 'imageSequence')
                }
                onEditNotes={() => setNotesOpen(true)}
                onRename={handleStartEditTitle}
                onToggleRequest={handleToggleRequestClick}
                onToggleAutoLoop={handleToggleAutoLoop}
                onDelete={handleDeleteClick}
              />
            ),
            unwrapped: true,
            disabled: false,
            testId: `media-overflow-${mediaItem.id}`,
          },
        ]
      : []),
    ...(hasSlides && selected && !hidePerRowSlideControl
      ? [
          {
            icon: (
              <SlideControlButton
                mediaId={mediaItem.id}
                entityType={contextParentType ?? parentType}
                entityId={contextParentId ?? parentId}
                disabled={isBaseDisabled}
                currentSlideIndex={currentSlideIndex}
                totalSlides={totalSlides}
                speakerNotes={speakerNotes}
                mediaType={resolveSlideControlMediaType(latestApprovedVersion?.fileType)}
                controlMode="local"
                frameSlideNumbers={localPreviewSlideAsset?.slidePreviews?.frameSlideNumbers}
              />
            ),
            unwrapped: true,
            disabled: false,
            tooltip: t('media.slideControl.tooltip' as TranslationKey),
            testId: `slide-control-${mediaItem.id}`,
          },
        ]
      : []),
    ...(eventMode === 'PERFORM'
      ? [
          {
            icon: isControlPending ? (
              <CircularProgress color="info" size={24} sx={{ width: '1.5rem', height: '1.5rem' }} />
            ) : selected ? (
              <StopCircleOutlined />
            ) : (
              <PlayCircleOutline />
            ),
            onClick: () => setPlayConfirmOpen(true),
            disabled:
              isBaseDisabled ||
              selected ||
              isControlPending ||
              playersPlayingType === undefined ||
              (!isVirtual && !latestApprovedVersion) ||
              !canTakeToAir,
            tooltip: selected ? undefined : t('media.play' as TranslationKey),
            testId: `play-media-${mediaItem.id}`,
          },
        ]
      : []),
  ];

  return (
    <>
      <CollapsibleBox
        sx={getSxStyles()}
        key={mediaItem.id}
        id={mediaItem.id}
        leadingAdornment={dragHandle}
        title={renderTitle()}
        onClick={handleTitleClick}
        headerActions={playlistHeaderActions}
        playActionButton={playActionButton}
        actionsRailWidth={isFlowControl ? FLOW_CONTROL_MEDIA_ACTIONS_RAIL_WIDTH_PX : undefined}
        isEditingTitle={isEditingTitle}
        showCollapseArrow={isFlowControl ? false : !isVirtual}
        collapseIcon={isVirtual ? <CloudQueue color={isVmRunning ? 'success' : 'disabled'} /> : undefined}
        disableCollapse={isFlowControl || isUploading || (!isVirtual && !hasAssets && !permissions.canUpload)}
        iconStart={
          isVirtual ? (
            (mediaItem as VirtualMediaItem).playType === PLAY_TYPE.LIVE ? (
              isPresenter ? (
                <ScreenShareOutlined color={isPlayerConnected ? 'error' : 'disabled'} />
              ) : (
                <Videocam color={isPlayerConnected ? 'error' : 'disabled'} />
              )
            ) : (
              <Description color="disabled" />
            )
          ) : (
            <MediaItemStatusIcon
              media={mediaItem as MediaDto}
              collapsedRowIconAsset={collapsedRowIconAsset}
              canUpload={permissions.canUpload}
              clickable={statusIconClickable && !statusIconBusy}
              onClick={handleStatusIconClick}
              contributorFacing={contributorFacing}
              busy={statusIconBusy}
              expanded={isRowExpanded}
            />
          )
        }
        actionButtons={isFlowControl ? [] : defaultActionButtons}
      >
        {isFlowControl ? null : isVirtual ? (
          <div style={{ padding: '1rem', color: 'text.disabled' }}>{mediaItem.title}</div>
        ) : (
          <>
            <MediaUploadFeedback mediaUpload={mediaUpload} />
            <ManageSessionMediaForm
              ref={manageMediaFormRef}
              media={mediaItem as MediaDto}
              onPreviewAsset={permissions.canPreview ? handlePreviewAsset : undefined}
              onUploadBusyChange={handleFormUploadBusyChange}
              onUploadSettled={handleFormUploadSettled}
              getInstructionsContext={(asset) =>
                mediaInstructionsContext
                  ? {
                      ...mediaInstructionsContext,
                      assetVersion: asset.version,
                    }
                  : undefined
              }
            />
          </>
        )}
      </CollapsibleBox>

      {previewModalOpen && previewData ? (
        <PreviewModal
          open={previewModalOpen}
          loading={previewLoading}
          onClose={closePreviewModal}
          playType={previewData.playType}
          playerSyncData={previewLoading ? undefined : previewData.playerSyncData}
          title={mediaItem.title}
          processingInstructions={previewInstructions}
          onPlay={(isFlowControl || eventMode === 'PERFORM') && canTakeToAir ? requestPlayFromPreview : undefined}
          playDisabled={
            isFlowControl
              ? Boolean(flowControlPlayAction?.disabled || flowControlPlayAction?.showStop || !canTakeToAir)
              : eventMode === 'PERFORM'
                ? Boolean(
                    isBaseDisabled ||
                    selected ||
                    isControlPending ||
                    playersPlayingType === undefined ||
                    (!isVirtual && !latestApprovedVersion) ||
                    !canTakeToAir,
                  )
                : true
          }
          slideNavigationScope="local"
        />
      ) : null}

      {liveKitPreviewData && (
        <LiveKitPreviewModal
          open={liveKitPreviewOpen}
          onClose={() => {
            setLiveKitPreviewOpen(false);
            setLiveKitPreviewData(null);
          }}
          livekitRoomId={liveKitPreviewData.livekitRoomId}
          title={liveKitPreviewData.title}
        />
      )}

      <SpeakerNotesDialog
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        mediaId={mediaItem.id}
        title={mediaItem.title}
      />
      <PlayCueConfirmationModal
        open={playConfirmOpen}
        mediaTitle={mediaItem.title}
        showCuePlayModes={showCuePlayModes}
        onJumpCue={handleConfirmJumpCue}
        onPlayInsert={handleConfirmPlayInsert}
        onConfirm={handleConfirmPlay}
        onCancel={() => setPlayConfirmOpen(false)}
        isLoading={isFlowControl ? Boolean(flowControlPlayAction?.showSpinner) : isControlPending}
      />

      {!isVirtual && rowInstructionsInsight && mediaInstructionsContext && compatibilityIssueAffordance ? (
        <MediaProcessingInstructionsModal
          open={instructionsOpen}
          onClose={() => setInstructionsOpen(false)}
          insight={rowInstructionsInsight}
          context={{ ...mediaInstructionsContext, assetVersion: compatibilityIssueAffordance.assetVersion }}
        />
      ) : null}

      {!isVirtual && (
        <ConfirmationModal
          open={isDeleteModalOpen}
          title={t('media.deleteMediaModalTitle')}
          text={t('media.deleteMediaModalText', { title: mediaItem.title })}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {!isVirtual && (
        <ConfirmationModal
          open={toggleRequestModalOpen}
          title={
            isRequested
              ? t('media.confirmUnrequestTitle' as TranslationKey)
              : t('media.confirmRequestTitle' as TranslationKey)
          }
          text={
            isRequested
              ? t('media.confirmUnrequestText' as TranslationKey, { title: (mediaItem as MediaDto).title })
              : t('media.confirmRequestText' as TranslationKey, { title: (mediaItem as MediaDto).title })
          }
          onConfirm={handleToggleRequestConfirm}
          onCancel={handleToggleRequestCancel}
          isLoading={isUpdatePending}
        />
      )}
      {!isVirtual && permissions.canUpload && (
        <VisuallyHiddenInput
          type="file"
          multiple
          onChange={handleFileUpload}
          ref={fileInputRef}
          disabled={mediaUpload.isBusy}
        />
      )}
      <AddMediaStorageModal
        open={quotaModals.isAddStorageModalOpen}
        onModalClose={() => quotaModals.setIsAddStorageModalOpen(false)}
      />
      <InsufficientStorageContactModal
        open={quotaModals.isContactModalOpen}
        onModalClose={() => quotaModals.setIsContactModalOpen(false)}
      />
    </>
  );
};
